/* Forced temporary password change.
 * Mounted at /change-password. Renders standalone (no Layout sidebar) because
 * the user must complete this before accessing the app.
 * Uses the same backend endpoint as Account Settings: POST /api/auth/change-password
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";
import PasswordRulesHint from "@/components/PasswordRulesHint";
import { validateNewPassword } from "@/lib/password";

export default function ChangeTemporaryPassword() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = validateNewPassword(form.next, form.confirm);
    if (v) { setErr(v); return; }
    if (form.current === form.next) {
      setErr("New password must be different from the current temporary password.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated. Welcome to FlowQA.");
      // Refresh user info — must_change_password should now be false
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch { /* fall through to redirect */ }
      navigate("/", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not change password.");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12" data-testid="change-temp-password-page">
      <div className="mx-auto max-w-md">
        <div className="mb-6 flex items-center gap-2 text-sm text-slate-700">
          <KeyRound className="size-4 text-teal-700" />
          <span>FlowQA — first-time sign-in</span>
        </div>
        <Card className="border-slate-200 p-6">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700">
              <ShieldCheck className="size-5" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-black tracking-tight text-slate-900">Change your temporary password</h1>
              <p className="mt-1 text-sm text-slate-600">
                Hi <b>{user?.name || user?.email || "there"}</b>. For your security, please replace the temporary password you were issued with one of your own.
              </p>
            </div>
          </div>

          <form className="mt-6 space-y-4" onSubmit={submit}>
            <div>
              <Label>Temporary password</Label>
              <Input
                type="password" autoComplete="current-password"
                value={form.current}
                onChange={(e) => setForm({ ...form, current: e.target.value })}
                data-testid="cp-current-input"
                required
              />
            </div>
            <div>
              <Label>New password</Label>
              <Input
                type="password" autoComplete="new-password"
                value={form.next}
                onChange={(e) => setForm({ ...form, next: e.target.value })}
                data-testid="cp-new-input"
                required
              />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input
                type="password" autoComplete="new-password"
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                data-testid="cp-confirm-input"
                required
              />
            </div>

            <PasswordRulesHint />

            {err && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" data-testid="cp-error">
                {err}
              </div>
            )}

            <Button type="submit" disabled={saving} className="w-full bg-teal-700 hover:bg-teal-800" data-testid="cp-submit-btn">
              {saving ? (<><Loader2 className="mr-2 size-4 animate-spin" /> Updating…</>) : "Change password and continue"}
            </Button>

            <button type="button" onClick={logout} className="block w-full text-center text-xs text-slate-500 underline-offset-2 hover:underline" data-testid="cp-signout-btn">
              Sign out
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}
